import React, { useEffect, useState } from "react";
import { Container, Box, CircularProgress, Typography } from "@mui/material";
import CustomBreadcrumbs from "../../common/components/breadcrumb-component-new/breadcrumb-component-new";
import UserProfileView from "./components/UserProfileView";
import UserProfileForm from "./components/UserProfileForm";
import { getUserProfileDetails } from "../../api/userProfile.api";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import "./user-profile.scss";

const UserProfilePage = () => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const [userProfileDetails, setUserProfileDetails] = useState<any>(null);
  const prefilledBillingDetails = location.state?.billingDetails;
  const fromOrderSummary = location.state?.fromOrderSummary;

  const isProfileCreated = Boolean(
    userProfileDetails?.first_name &&
    userProfileDetails?.last_name &&
    userProfileDetails?.email &&
    userProfileDetails?.mobileNumber,
  );

  const getBreadcrumbItems = () => {
    if (!isProfileCreated) return [{ label: "User Profile" }];
    if (isEditMode)
      return [
        {
          label: "User Profile",
          to: "/",
          onClick: () => setIsEditMode(false),
        },
        {
          label: "Edit Profile",
        },
      ];

    return [{ label: "User Profile" }];
  };

  const fetchUserProfileDetails = async () => {
    try {
      setLoading(true);

      const response = await getUserProfileDetails();
      if (response?.data?.success) {
        const profileData = response.data.data;
        setUserProfileDetails(profileData);

        const userDetailsStr = localStorage.getItem("userDetails");

        if (userDetailsStr) {
          const userDetails = JSON.parse(userDetailsStr);

          const updatedUser = {
            ...userDetails.user,
            profile_photo:
              profileData?.profile_photo ?? userDetails.user?.profile_photo,
            first_name: profileData?.first_name ?? userDetails.user?.first_name,
            last_name: profileData?.last_name ?? userDetails.user?.last_name,
            email: profileData?.email ?? userDetails.user?.email,
            mobileNumber:
              profileData?.mobileNumber ?? userDetails.user?.mobileNumber,
          };

          // update only if something actually changed
          const hasChanges =
            userDetails.user?.profile_photo !== updatedUser?.profile_photo ||
            userDetails.user?.first_name !== updatedUser?.first_name ||
            userDetails.user?.last_name !== updatedUser?.last_name ||
            userDetails.user?.email !== updatedUser?.email ||
            userDetails.user?.mobileNumber !== updatedUser?.mobileNumber;
          if (hasChanges) {
            localStorage.setItem(
              "userDetails",
              JSON.stringify({
                ...userDetails,
                user: updatedUser,
              }),
            );
          }
        }
      }
    } catch (error) {
      console.error("USER PROFILE GET FAILED::", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    fetchUserProfileDetails().then(() => {
      if (fromOrderSummary && !isEditMode) {
        setIsEditMode(true);
      }
    });
  }, []);

  // ---- Render Content ----
  const renderContent = () => {
    if (!isEditMode) {
      return (
        <UserProfileView
          isEditMode={isEditMode}
          onEdit={() => setIsEditMode(true)}
          userProfileDetails={userProfileDetails}
        />
      );
    }

    return (
      <UserProfileForm
        isProfileCreated={isProfileCreated}
        userProfileDetails={userProfileDetails}
        setUserProfileDetails={setUserProfileDetails}
        setIsEditMode={setIsEditMode}
        onCancel={() => setIsEditMode(false)}
        onSave={() => setIsEditMode(false)}
        fetchUserProfileDetails={fetchUserProfileDetails}
        prefilledBillingDetails={prefilledBillingDetails}
      />
    );
  };

  return (
    <Container className="user-profile-page">
      <Box className="user-profile-page__back" onClick={handleBack}>
        <ArrowBack className="user-profile-page__back-icon" />
        <Typography className="user-profile-page__back-text">Back</Typography>
      </Box>
      <Box className="user-profile-page__breadcrumbs">
        <CustomBreadcrumbs items={getBreadcrumbItems()} />
      </Box>

      <Box className="user-profile-page__content">
        {loading ? (
          <Box className="user-profile-page__spinner-container">
            <CircularProgress
              size="3rem"
              className="spinner-details-page__spinner"
            />
          </Box>
        ) : (
          renderContent()
        )}
      </Box>
    </Container>
  );
};

export default UserProfilePage;
